import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Users, ThumbsUp, Star } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useConfirmation } from '@/contexts/confirmation'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    SURVEY_FILTER_DEFAULTS,
    SurveyFilters,
    getQuarterPeriod,
} from '../../utils/report-filters'
import { useSurveyFilterForm } from '../../hooks/use-survey-filter-form'
import {
    exportQuarterlyReportExcel,
    getQuarterlyReport,
} from '../../services/quarterly-report'
import { QuarterlyInsightResponse } from '../../services/quarterly-report-gemini'
import AiQuarterlyInsights from './components/ai-quarterly-insights'
import { CustomerFlowChart } from './components/customer-flow-chart'
import { QuarterlyDataTable } from './components/quarterly-data-table'
import { ReferralUpsaleChart } from './components/referral-upsale-chart'
import { ReportFilters } from './components/report-filters'
import { SatisfactionRatingsChart } from './components/satisfaction-ratings-chart'

const useReportFilters = () => {
    const [searchParams] = useSearchParams()
    return useMemo(() => getQuarterPeriod(searchParams), [searchParams])
}

const QuarterlyReport = () => {
    const { t } = useTranslation('surveys')
    const period = useReportFilters()
    const { periodRange } = period
    const { setConfirmation } = useConfirmation()
    const { filters: urlFilters, setFilters } =
        useUrlSearchParams<SurveyFilters>({
            filterDefaults: SURVEY_FILTER_DEFAULTS,
        })
    const { control, filters: owners } = useSurveyFilterForm(
        urlFilters,
        setFilters
    )

    const filters = useMemo(
        () => ({
            from_date: period.from_date,
            to_date: period.to_date,
            employee_ids: owners.employee_ids,
            customer_sale_followers: owners.customer_sale_followers,
            customer_cs_followers: owners.customer_cs_followers,
        }),
        [period.from_date, period.to_date, owners]
    )

    const {
        data: report,
        renderState,
        isFetching,
        isError,
        refetch,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SURVEYS, 'quarterly-report', filters],
        queryFn: () => getQuarterlyReport(filters),
    })
    const data = report?.quarters ?? []
    const [insightsLoading, setInsightsLoading] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const filterKey = JSON.stringify(filters)
    const insightsRef = useRef<{
        key: string
        insights: QuarterlyInsightResponse
    } | null>(null)

    const doExport = async (withInsights = true) => {
        setIsExporting(true)
        try {
            await exportQuarterlyReportExcel(
                filters,
                withInsights && insightsRef.current?.key === filterKey
                    ? insightsRef.current.insights
                    : null
            )
            toast.success(t('surveys.quarterly_report.export_success'))
        } catch {
            toast.error(t('surveys.quarterly_report.export_error'))
        } finally {
            setIsExporting(false)
        }
    }

    const handleExport = () => {
        if (insightsLoading) {
            setConfirmation({
                open: true,
                title: t(
                    'surveys.quarterly_report.export_insights_loading_title'
                ),
                description: t(
                    'surveys.quarterly_report.export_insights_loading_confirm'
                ),
                onConfirm: () => doExport(false),
            })
            return
        }
        doExport()
    }

    const totalParticipants = data.reduce(
        (sum, quarter) => sum + quarter.survey_participants,
        0
    )
    const summaryCards = [
        {
            icon: Users,
            label: t('surveys.quarterly_report.total_participants'),
            value: totalParticipants,
            color: 'text-blue-600 bg-blue-50',
        },
        {
            icon: ThumbsUp,
            label: t('surveys.quarterly_report.high_satisfaction'),
            value:
                report?.overall_high_satisfaction_rate != null
                    ? `${report.overall_high_satisfaction_rate.toFixed(1)}%`
                    : '—',
            color: 'text-emerald-600 bg-emerald-50',
        },
        {
            icon: Star,
            label: t('surveys.quarterly_report.avg_service_rating'),
            value:
                report?.overall_avg_service_rating != null
                    ? `${report.overall_avg_service_rating.toFixed(1)}/10`
                    : '—',
            color: 'text-amber-600 bg-amber-50',
        },
    ]
    const stateComponent = renderState()

    return (
        <div className="mt-2 space-y-2">
            <ReportFilters
                control={control}
                onExport={
                    !isError && report && data.length > 0
                        ? handleExport
                        : undefined
                }
                onRefresh={refetch}
                loading={isFetching || isExporting}
                insightsLoading={insightsLoading}
            />
            {stateComponent || (
                <>
                    <p className="text-xs text-muted-foreground">
                        {t('surveys.quarterly_report.customer_movement_scope')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {summaryCards.map(
                            ({ icon: Icon, label, value, color }) => (
                                <Card
                                    key={label}
                                    className="p-4 flex items-center gap-4 border border-gray-100"
                                >
                                    <div
                                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}
                                    >
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium text-gray-500">
                                            {label}
                                        </div>
                                        <div className="text-xl font-semibold text-gray-900">
                                            {isFetching ? (
                                                <Skeleton className="h-6 w-16" />
                                            ) : (
                                                value
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            )
                        )}
                    </div>
                    {data.length > 0 ? (
                        <ErrorBoundary>
                            <div className="space-y-2">
                                <Card className="p-4 border border-gray-100">
                                    <p className="mb-2 text-xs text-muted-foreground">
                                        {t(
                                            'surveys.quarterly_report.customer_movement_scope'
                                        )}
                                    </p>
                                    <QuarterlyDataTable data={data} />
                                </Card>
                                <CustomerFlowChart data={data} />
                                <SatisfactionRatingsChart data={data} />
                                <ReferralUpsaleChart data={data} />
                                <AiQuarterlyInsights
                                    key={JSON.stringify(filters)}
                                    data={data}
                                    periodRange={periodRange}
                                    onLoadingChange={setInsightsLoading}
                                    onInsightsReady={(insights) => {
                                        insightsRef.current = insights
                                            ? { key: filterKey, insights }
                                            : null
                                    }}
                                />
                            </div>
                        </ErrorBoundary>
                    ) : (
                        <Card className="p-8 border border-gray-100 text-center text-gray-400 text-sm">
                            {t('surveys.quarterly_report.no_data')}
                        </Card>
                    )}
                </>
            )}
        </div>
    )
}

export default QuarterlyReport
